import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FiChevronRight, FiChevronDown, FiSearch, FiX, 
  FiFilter, FiDollarSign, FiTag
} from 'react-icons/fi';
import { useSolution } from '../../../context/SolutionContext';

const ListView = () => {
  const { state, dispatch } = useSolution();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterPhase, setFilterPhase] = useState('all');
  const [expandedPhases, setExpandedPhases] = useState(new Set());
  const [expandedLayers, setExpandedLayers] = useState(new Set());
  const [expandedSubLayers, setExpandedSubLayers] = useState(new Set());
  
  const togglePhase = (id) => {
    const newSet = new Set(expandedPhases);
    if (newSet.has(id)) newSet.delete(id);
    else newSet.add(id);
    setExpandedPhases(newSet);
  };
  
  const toggleLayer = (id) => {
    const newSet = new Set(expandedLayers);
    if (newSet.has(id)) newSet.delete(id);
    else newSet.add(id);
    setExpandedLayers(newSet);
  };
  
  const toggleSubLayer = (id) => {
    const newSet = new Set(expandedSubLayers);
    if (newSet.has(id)) newSet.delete(id);
    else newSet.add(id);
    setExpandedSubLayers(newSet);
  };
  
  // Group components
  const grouped = {};
  state.selectedComponents.forEach(comp => {
    if (filterPhase !== 'all' && comp.phaseId !== filterPhase) return;
    if (searchTerm && !comp.name.toLowerCase().includes(searchTerm.toLowerCase())) return;
    
    const phaseKey = comp.phaseId;
    const layerKey = comp.layerId;
    const subLayerKey = comp.subLayerId;
    
    if (!grouped[phaseKey]) {
      grouped[phaseKey] = { info: comp, layers: {} };
    }
    if (!grouped[phaseKey].layers[layerKey]) {
      grouped[phaseKey].layers[layerKey] = { info: comp, subLayers: {} };
    }
    if (!grouped[phaseKey].layers[layerKey].subLayers[subLayerKey]) {
      grouped[phaseKey].layers[layerKey].subLayers[subLayerKey] = [];
    }
    grouped[phaseKey].layers[layerKey].subLayers[subLayerKey].push(comp);
  });
  
  const phases = Object.values(grouped);
  
  return (
    <div className="h-full flex flex-col bg-gray-50">
      {/* Toolbar */}
      <div className="bg-white border-b border-gray-200 p-4">
        <div className="flex items-center gap-4">
          <div className="flex-1 relative">
            <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search components..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          
          <select
            value={filterPhase}
            onChange={(e) => setFilterPhase(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">All Phases</option>
            {[...new Set(state.selectedComponents.map(c => c.phaseId))].map(phaseId => (
              <option key={phaseId} value={phaseId}>
                {state.selectedComponents.find(c => c.phaseId === phaseId)?.phaseName}
              </option>
            ))}
          </select>
        </div>
      </div>
      
      {/* List Content */}
      <div className="flex-1 overflow-y-auto p-4">
        {phases.length === 0 ? (
          <div className="text-center text-gray-500 py-20">
            <p className="text-lg">No components selected</p>
            <p className="text-sm">Select components from the sidebar to view them here</p>
          </div>
        ) : (
          <div className="space-y-4">
            {phases.map((phase, pi) => {
              const isPhaseExpanded = expandedPhases.has(phase.info.phaseId);
              const phaseLayers = Object.values(phase.layers);
              
              return (
                <div key={phase.info.phaseId} className="bg-white rounded-xl shadow-sm border border-gray-200">
                  <button
                    onClick={() => togglePhase(phase.info.phaseId)}
                    className="w-full flex items-center justify-between p-4 hover:bg-gray-50 rounded-t-xl"
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-2xl">{phase.info.phaseIcon || '📦'}</span>
                      <div className="text-left">
                        <div className="font-semibold text-gray-900">{phase.info.phaseName}</div>
                        <div className="text-sm text-gray-500">
                          {phaseLayers.length} layers, {phaseLayers.reduce((sum, l) => sum + Object.values(l.subLayers).flat().length, 0)} components
                        </div>
                      </div>
                    </div>
                    {isPhaseExpanded ? <FiChevronDown /> : <FiChevronRight />}
                  </button>
                  
                  <AnimatePresence>
                    {isPhaseExpanded && (
                      <motion.div
                        initial={{ height: 0 }}
                        animate={{ height: 'auto' }}
                        exit={{ height: 0 }}
                        className="overflow-hidden"
                      >
                        {phaseLayers.map((layer, li) => {
                          const isLayerExpanded = expandedLayers.has(layer.info.layerId);
                          const layerSubLayers = Object.values(layer.subLayers);
                          
                          return (
                            <div key={layer.info.layerId} className="border-t border-gray-100">
                              <button
                                onClick={() => toggleLayer(layer.info.layerId)}
                                className="w-full flex items-center justify-between p-3 pl-12 hover:bg-gray-50"
                              >
                                <div className="flex items-center gap-2">
                                  <span>📦</span>
                                  <span className="font-medium text-gray-800">{layer.info.layerName}</span>
                                  <span className="text-sm text-gray-500">
                                    ({layerSubLayers.reduce((sum, sl) => sum + sl.length, 0)} components)
                                  </span>
                                </div>
                                {isLayerExpanded ? <FiChevronDown /> : <FiChevronRight />}
                              </button>
                              
                              <AnimatePresence>
                                {isLayerExpanded && (
                                  <motion.div
                                    initial={{ height: 0 }}
                                    animate={{ height: 'auto' }}
                                    exit={{ height: 0 }}
                                    className="overflow-hidden"
                                  >
                                    {layerSubLayers.map((components, sli) => {
                                      const subLayerInfo = components[0];
                                      const isSubLayerExpanded = expandedSubLayers.has(subLayerInfo.subLayerId);
                                      
                                      return (
                                        <div key={subLayerInfo.subLayerId} className="border-t border-gray-50">
                                          <button
                                            onClick={() => toggleSubLayer(subLayerInfo.subLayerId)}
                                            className="w-full flex items-center justify-between p-3 pl-20 hover:bg-gray-50"
                                          >
                                            <span className="text-sm text-gray-700">📁 {subLayerInfo.subLayerName}</span>
                                            {isSubLayerExpanded ? <FiChevronDown /> : <FiChevronRight />}
                                          </button>
                                          
                                          <AnimatePresence>
                                            {isSubLayerExpanded && (
                                              <motion.div
                                                initial={{ height: 0 }}
                                                animate={{ height: 'auto' }}
                                                exit={{ height: 0 }}
                                                className="overflow-hidden"
                                              >
                                                {components.map(comp => (
                                                  <div
                                                    key={comp.id}
                                                    className="flex items-center justify-between p-3 pl-28 hover:bg-blue-50 cursor-pointer"
                                                    onClick={() => dispatch({ type: 'SHOW_COMPONENT_DETAIL', payload: comp })}
                                                  >
                                                    <div className="flex items-center gap-3">
                                                      <span>{comp.icon}</span>
                                                      <div>
                                                        <div className="font-medium text-gray-900">{comp.name}</div>
                                                        <div className="text-xs text-gray-500">{comp.purpose}</div>
                                                      </div>
                                                    </div>
                                                    <div className="flex items-center gap-4">
                                                      <span className="text-sm text-green-600">
                                                        ${comp.cost?.low || 0} - ${comp.cost?.high || 0}
                                                      </span>
                                                      <button
                                                        onClick={(e) => {
                                                          e.stopPropagation();
                                                          dispatch({ type: 'REMOVE_COMPONENT', payload: comp });
                                                        }}
                                                        className="p-1 hover:bg-red-100 rounded"
                                                      >
                                                        <FiX className="w-4 h-4 text-red-500" />
                                                      </button>
                                                    </div>
                                                  </div>
                                                ))}
                                              </motion.div>
                                            )}
                                          </AnimatePresence>
                                        </div>
                                      );
                                    })}
                                  </motion.div>
                                )}
                              </AnimatePresence>
                            </div>
                          );
                        })}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default ListView;